'use client'

import { useState, type ReactNode } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import Footer from '@/components/Footer'
import WhitelabelCTA from '@/components/services/WhitelabelCTA'
import WebsiteUrlInput from '@/components/forms/WebsiteUrlInput'
import { reportError } from '@/lib/utils/error-notification'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'

const PDF_HREF = '/downloads/free-seo-checklist.pdf'
const COVER_SRC = '/images/resources/cover-flat.jpg'

const STATS = [
  { value: '13', label: 'pages' },
  { value: '~190', label: 'checkpoints' },
  { value: '16', label: 'sections' },
] as const

const SECTIONS: { name: string; blurb: string }[] = [
  { name: '12 Highest-Leverage Moves', blurb: 'The front page: the dozen actions that move rankings and leads first.' },
  { name: 'Strategy & Foundations', blurb: 'Baseline capture before you touch anything — geo-grid snapshot, NAP source of truth, competitor category audit.' },
  { name: 'Google Business Profile', blurb: 'Video verification prep, suspension evidence packs, category side-effects, UTM per surface, Reserve with Google, LSA.' },
  { name: 'NAP, Citations & Consistency', blurb: 'One source of truth, directory hygiene, and the mismatches that tank local pack trust.' },
  { name: 'Reviews & Reputation', blurb: 'Ask flow, response cadence, and what is now illegal under 16 CFR Part 465.' },
  { name: 'On-Page Local SEO', blurb: 'Location pages, service pages, internal links, and intent matching.' },
  { name: 'Structured Data & Technical', blurb: 'LocalBusiness schema, crawl/index health, Core Web Vitals, and site architecture.' },
  { name: 'Content & Topical Authority', blurb: 'What to publish so you own the category — not just a handful of keywords.' },
  { name: 'Tracking, DNI & Conversions', blurb: 'Call tracking that does not break NAP, server-side tagging, offline conversion import.' },
  { name: 'Multi-Location & Franchise', blurb: 'Entity design, location page templates, and brand vs. location GBP rules.' },
  { name: 'AI Search & LLM Visibility', blurb: 'How you show up in ChatGPT, Perplexity, and Google AI Overviews.' },
  { name: 'Local Pack & Maps', blurb: 'Proximity, prominence, and the signals that actually move the map 3-pack.' },
  { name: 'Links, Mentions & PR', blurb: 'Earned citations and mentions that look like a real business, not a link scheme.' },
  { name: 'Conversion Paths & UX', blurb: 'From pack click to booked job — forms, click-to-call, hours, and offer clarity.' },
  { name: 'Operating Cadence', blurb: 'A table for who does what, how often, and what “done this month” looks like.' },
  { name: 'Risk Register', blurb: 'Eight tempting tactics, named plainly — exposure, and the legitimate alternative.' },
]

function CoverStack() {
  return (
    <div className="seo-cover-stage">
      <div className="seo-cover-stack">
        <div className="seo-cover-card" aria-hidden />
        <div className="seo-cover-card" aria-hidden />
        <div className="seo-cover-card">
          <Image
            src={COVER_SRC}
            alt="The Complete Local SEO Checklist cover"
            fill
            className="object-cover"
            priority
            sizes="300px"
          />
        </div>
        <div className="seo-cover-card" aria-hidden />
        <div className="seo-cover-card" aria-hidden />
      </div>
    </div>
  )
}

function LeadForm() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string; website?: string }>({})

  const validate = () => {
    const next: typeof fieldErrors = {}
    if (!name.trim() || name.trim().length < 2) next.name = 'Please enter your name'
    if (/\d/.test(name.trim())) next.name = 'Name cannot contain numbers'
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = 'Please enter a valid email'
    if (!website.trim() || !website.includes('.')) next.website = 'Please enter your website'
    setFieldErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!validate()) return

    setIsSubmitting(true)
    try {
      const res = await fetch('/api/form-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          website: website.trim(),
          focusArea: 'Requested the Free SEO Checklist PDF',
          serviceName: 'Free SEO Checklist',
          servicePrice: 0,
          source: 'free-seo-checklist',
        }),
      })
      if (!res.ok) {
        setError('Something went wrong. Please try again.')
        return
      }
      trackPostHogEvent('form_submitted', {
        form_source: 'free-seo-checklist',
        service_name: 'Free SEO Checklist',
        has_website: true,
      })
      setIsSubmitted(true)
    } catch (err) {
      reportError(err instanceof Error ? err : new Error('SEO checklist form failed'), {
        custom: { source: 'free-seo-checklist' },
      })
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSubmitted) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
        <p className="text-2xl" aria-hidden>
          ✓
        </p>
        <h3 className="mt-2 text-lg font-bold text-slate-900">You&apos;re in</h3>
        <p className="mt-2 text-sm text-slate-600">
          Grab the 13-page checklist below.
        </p>
        <a
          href={PDF_HREF}
          download="SmartMetrics-Free-SEO-Checklist.pdf"
          className="mt-4 inline-flex items-center justify-center rounded-full bg-[#13479c] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0f3a7a]"
        >
          Download Free Checklist
        </a>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" id="download">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="seo-checklist-name" className="sr-only">
            Full name
          </label>
          <input
            id="seo-checklist-name"
            type="text"
            autoComplete="name"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full rounded-lg border px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-[#13479c] focus:outline-none focus:ring-1 focus:ring-[#13479c] ${
              fieldErrors.name ? 'border-red-500' : 'border-slate-300'
            }`}
          />
          {fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}
        </div>
        <div>
          <label htmlFor="seo-checklist-email" className="sr-only">
            Email
          </label>
          <input
            id="seo-checklist-email"
            type="email"
            autoComplete="email"
            placeholder="Work email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full rounded-lg border px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-[#13479c] focus:outline-none focus:ring-1 focus:ring-[#13479c] ${
              fieldErrors.email ? 'border-red-500' : 'border-slate-300'
            }`}
          />
          {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
        </div>
      </div>
      <div className={fieldErrors.website ? '[&>div>div]:border-red-500' : ''}>
        <WebsiteUrlInput id="seo-checklist-website" label="" value={website} placeholder="yourwebsite.com" onChange={setWebsite} />
        {fieldErrors.website && <p className="mt-1 text-xs text-red-600">{fieldErrors.website}</p>}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-full bg-[#13479c] px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(19,71,156,0.28)] hover:bg-[#0f3a7a] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting ? 'Submitting…' : 'Download Free Checklist'}
      </button>
      <p className="text-center text-[11px] text-slate-500">No spam. Instant PDF download.</p>
    </form>
  )
}

export default function FreeSeoChecklistPage({ children }: { children?: ReactNode }) {
  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <nav className="mb-6 text-sm text-muted-foreground">
            <Link href="/" className="hover:text-foreground">
              Homepage
            </Link>
            <span className="mx-2">/</span>
            <Link href="/resources" className="hover:text-foreground">
              Resources
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Free SEO Checklist</span>
          </nav>

          <section className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-14">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#13479c]">Free download</p>
              <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
                The complete SEO checklist for your site
              </h1>
              <p className="mt-4 max-w-xl text-lg leading-relaxed text-slate-600">
                13 pages, ~190 checkpoints across 16 sections. Written as a build sequence, not a grab bag.
              </p>
              <div className="mt-6 flex flex-wrap gap-6">
                {STATS.map((s) => (
                  <div key={s.label}>
                    <div className="text-2xl font-bold tracking-tight text-slate-900">{s.value}</div>
                    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{s.label}</div>
                  </div>
                ))}
              </div>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-500 sm:text-lg">
                Starts with the 12 highest-leverage moves. Then Strategy &amp; Foundations before anyone touches the
                profile. Ends with an operating cadence and a risk register for the shortcuts people always ask about.
              </p>
            </div>

            <div>
              <CoverStack />
              <h2 className="mb-3 text-base font-semibold text-slate-900">Get the PDF</h2>
              <LeadForm />
            </div>
          </section>

          <section className="mt-16">
            <h2 className="text-2xl font-bold text-slate-900">16 sections, in build order</h2>
            <p className="mt-2 max-w-2xl text-slate-600">
              The original 7 categories became 16, dependency-ordered so it reads as a sequence you can run — not a
              pile of unrelated tasks.
            </p>
            <ol className="mt-8 grid gap-3 sm:grid-cols-2">
              {SECTIONS.map((section, i) => (
                <li
                  key={section.name}
                  className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <span className="w-7 shrink-0 text-sm font-bold text-[#13479c]">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{section.name}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{section.blurb}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="mt-16">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-xl font-bold text-slate-900">Why the Risk Register exists</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Grey-hat tactics were rewritten as a Risk Register — same eight moves, named plainly, each with why it
                is tempting, the actual exposure, and the legitimate alternative.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                Since{' '}
                <a
                  href="https://www.ecfr.gov/current/title-16/chapter-I/subchapter-D/part-465"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-slate-900 underline decoration-slate-300 underline-offset-2 hover:text-[#13479c] hover:decoration-[#13479c]"
                >
                  16 CFR Part 465
                </a>{' '}
                took effect in October
                2024, review gating and incentivized reviews carry civil penalties north of $50k per violation. Each
                review can count separately, and the rule names the business <em>and</em> anyone acting on its behalf
                as liable. Fake reviews or negative SEO aimed at competitors can go further, into tortious interference.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                Every site owner eventually asks why a spammy competitor is outranking them. This page answers it — and
                draws the line between what actually works and what will get a site penalized.
              </p>
            </div>
          </section>

          {children}

          <section className="mt-16 rounded-2xl bg-[#071631] px-6 py-10 text-center sm:px-10">
            <h2 className="text-2xl font-bold text-white">Ready to run the sequence?</h2>
            <p className="mx-auto mt-2 max-w-lg text-sm text-white/70">
              Get the 13-page checklist, then use it as the punch list for your site — solo or with your team.
            </p>
            <a
              href="#download"
              className="mt-6 inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#13479c] hover:bg-slate-100"
            >
              Download Free Checklist
            </a>
          </section>
        </div>
      </div>

      <section className="bg-[#071631] pt-16 sm:pt-20 pb-0">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <WhitelabelCTA embedded heading="Get a Quote" buttonText="Get a Quote" />
        </div>
        <Footer variant="onDark" />
      </section>
    </>
  )
}
